import type { Chord } from '@tunasong/music-lib'
import { ChordLib, ScaleLib, analyzeChord, detectModalSignature, isChordVariant } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import cn from 'classnames'
import type { CSSProperties, FC, MouseEvent } from 'react'
import React from 'react'
import { ChordDegree } from '../note/index.js'
import { getChordConfidenceClassName } from './chord-confidence.js'
import styles from './simple-chord.module.css'

/**
 * Suffix appended to the chip's aria-label when the chord carries a modal
 * characteristic note. Mirrors Joni's UX spec §2.3: "<chord name>, modal —
 * Dorian signature". Capitalises the mode name; the noun "signature" is the
 * level-independent identifier (the lexicon `tagLabel` carries level-specific
 * variants used in the WindowTag chip, not here).
 */
const buildModalAriaSuffix = (mode: string): string => {
  const capitalised = mode.charAt(0).toUpperCase() + mode.slice(1)
  return `modal — ${capitalised} signature`
}

export interface SimpleChordProps {
  chord: Chord
  /** Transpose the display by a number of semitones ( + / - ) */
  transpose?: number
  /** Display transposition as part of the chord (+/-)transposition */
  showTranspose?: boolean
  scale?: Scale | null
  selected?: boolean
  focused?: boolean
  className?: string
  style?: CSSProperties
  onClick?(chord: Chord): void
}

export const SimpleChord: FC<SimpleChordProps> = props => {
  const {
    chord,
    scale,
    showTranspose: _showTranspose = false,
    transpose = 0,
    selected = false,
    focused = false,
    className,
    style,
    onClick,
  } = props

  const chordName = ChordLib.getName(chord, transpose)

  const inScale = scale ? ScaleLib.inScale(scale, chord) : true
  /**
   * Use the theory analyzer to compute the chord's function relative to the
   * established scale. analyzeChord covers diatonic, parallel-mode borrowings,
   * secondary dominants, and chromatic outliers — so the superscript matches
   * what the InlineCaption / Caption / Sidebar surfaces show. We don't know
   * that the root is in the custom chord; analyzeChord returns null for those.
   */
  const analysis = isChordVariant(chord) && scale ? analyzeChord({ chord, scale }) : null

  /**
   * Per-chord S3a (modal characteristic-note glow). The detector returns an
   * array — one entry per qualifying chord — and we call it with a single-chord
   * window because the per-chord chip only needs to know about itself. The
   * helper is O(notes-per-chord) and runs once per render; React Compiler
   * memoizes the chip subtree so this stays cheap.
   */
  const modalHit = scale ? detectModalSignature([chord], scale)[0] ?? null : null

  const handleClick = onClick
    ? (ev: MouseEvent) => {
        ev.preventDefault()
        onClick(chord)
      }
    : undefined

  /* Screen-reader hint: out-of-key chords carry a visual signal (color + dotted
   * underline). The aria-label suffix ensures keyboard / SR users get the same
   * information without relying on the visible styling. When the chord ALSO
   * carries a modal signature, append the Joni §2.3 suffix so SR users hear
   * the modal context without needing to open the popup.
   */
  const ariaLabelParts: string[] = []
  if (!inScale) {
    ariaLabelParts.push(`${chordName} (outside the key)`)
  }
  if (modalHit) {
    ariaLabelParts.push(`${chordName}, ${buildModalAriaSuffix(modalHit.mode)}`)
  }
  const ariaLabel = ariaLabelParts.length > 0 ? ariaLabelParts.join('. ') : undefined

  return (
    <span
      contentEditable={false}
      onClick={handleClick}
      aria-label={ariaLabel}
      data-modal-signature={modalHit?.mode}
      className={cn(
        styles.chord,
        inScale ? styles['chord--inScale'] : styles['chord--outOfScale'],
        selected && focused && styles['chord--selected'],
        onClick && styles['chord--clickable'],
        modalHit && styles['chord--modal'],
        className
      )}
      style={style}
    >
      {chordName}
      {analysis && analysis.chordFunction !== 'chromatic' ? (
        <ChordDegree
          chordFunction={analysis.chordFunction}
          className={getChordConfidenceClassName(analysis.confidence)}
        />
      ) : null}
    </span>
  )
}
